import { Fragment } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { amount, dateLabel, useBooks } from "@/lib/books";
import { Button } from "@/components/ui/button";
import { Loaded } from "@/components/books/ui";

/** Title Zoho would print for each kind of Tally voucher. */
const TITLES = {
  Sales: "Tax Invoice", Purchase: "Bill", Receipt: "Payment Receipt", Payment: "Payment Voucher",
  "Credit Note": "Credit Note", "Debit Note": "Debit Note", Journal: "Journal", Contra: "Contra",
  "Sales Order": "Sales Order", "Purchase Order": "Purchase Order", "Delivery Note": "Delivery Challan",
};

/** Where an item voucher's goods are booked: the rest of its lines are taxes and charges. */
const TRADING = { Sales: "Sales Accounts", "Credit Note": "Sales Accounts", Purchase: "Purchase Accounts", "Debit Note": "Purchase Accounts" };

/** Invoice-style totals: Sub total, then each tax / charge / round-off line, then Total. */
function totalsOf(v) {
  const trading = TRADING[v.base_type];
  if (!trading || !v.inventory.length) return null;
  // Sales and credit notes carry credits on these lines; purchases and debit notes, debits.
  const credit = trading === "Sales Accounts" ? v.base_type === "Sales" : v.base_type !== "Purchase";
  const charges = v.lines
    .filter((l) => !l.is_party && l.ledger_guid !== v.party_guid && l.section !== trading)
    .map((l) => ({ name: l.ledger, amount: credit ? l.credit - l.debit : l.debit - l.credit }));
  return { subTotal: v.amount - charges.reduce((a, c) => a + c.amount, 0), charges };
}

/** One line per ledger where Tally split a ledger across items (no bills or bank details to keep apart). */
function mergedLines(lines) {
  const out = [];
  lines.forEach((l) => {
    const same = !l.bills.length && !l.bank.length && out.find((o) => o.ledger === l.ledger && o.ledger_guid === l.ledger_guid
      && !o.bills.length && !o.bank.length && Boolean(o.debit) === Boolean(l.debit));
    if (same) {
      same.debit += l.debit;
      same.credit += l.credit;
    } else {
      out.push({ ...l });
    }
  });
  return out;
}

/** A read-only copy of one Tally voucher, laid out as a document. */
export default function VoucherView() {
  const { guid } = useParams();
  const navigate = useNavigate();
  const { organization } = useAuth();
  const state = useBooks(`/books/vouchers/${guid}`);
  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex items-center justify-between gap-2 print:hidden">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={15} /> Back
        </button>
        <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5"><Printer size={14} /> Print</Button>
      </div>
      <Loaded state={state}>
        {state.data && (() => {
          const v = state.data;
          const title = TITLES[v.base_type] || v.voucher_type;
          const buyer = v.buyer || {};
          const address = buyer.address?.length ? buyer.address : v.party_address;
          const totals = totalsOf(v);
          return (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm print:border-0 print:shadow-none">
              {(v.is_cancelled || v.is_optional || !v.balanced) && (
                <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-sm text-amber-900">
                  <AlertTriangle size={15} />
                  {v.is_cancelled ? "This voucher is cancelled in Tally." : v.is_optional ? "This is an optional voucher — it does not affect the books."
                    : "The lines of this voucher do not balance as read from Tally."}
                </div>
              )}
              <div className="flex flex-wrap items-start justify-between gap-6 border-b border-slate-100 p-8">
                <div>
                  <div className="text-lg font-semibold">{organization?.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{v.voucher_type}{v.voucher_type !== v.base_type ? ` (${v.base_type})` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-light uppercase tracking-wide text-slate-800">{title}</div>
                  <div className="mt-1 text-sm font-semibold"># {v.number || "—"}</div>
                  <div className="mt-2 text-xs text-slate-500">Amount</div>
                  <div className="text-xl font-semibold tabular-nums">₹{amount(v.amount)}</div>
                </div>
              </div>

              <div className="grid gap-6 border-b border-slate-100 p-8 sm:grid-cols-2">
                <div className="text-sm">
                  {v.party && (
                    <>
                      <div className="text-xs text-slate-500">{v.base_type === "Purchase" || v.base_type === "Payment" ? "From / To" : "Bill to"}</div>
                      {v.party_guid
                        ? <Link to={`/accounts/${v.party_guid}`} className="font-semibold text-blue-700 hover:underline">{buyer.name || v.party}</Link>
                        : <div className="font-semibold">{v.party}</div>}
                      {(address || []).map((line, i) => <div key={i} className="text-slate-600">{line}</div>)}
                      {(buyer.state || buyer.pincode) && <div className="text-slate-600">{[buyer.state, buyer.pincode].filter(Boolean).join(" - ")}</div>}
                      {(buyer.gstin || v.party_gstin) && <div className="mt-1 font-mono text-xs">GSTIN {buyer.gstin || v.party_gstin}</div>}
                    </>
                  )}
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 self-start text-sm sm:justify-self-end">
                  <dt className="text-slate-500">Date</dt><dd>{dateLabel(v.date)}</dd>
                  {v.reference && (<><dt className="text-slate-500">Reference</dt><dd>{v.reference}{v.reference_date ? ` · ${dateLabel(v.reference_date)}` : ""}</dd></>)}
                  {v.order_ref && (<><dt className="text-slate-500">Order no.</dt><dd>{v.order_ref}</dd></>)}
                  {v.due_text && (<><dt className="text-slate-500">Due</dt><dd>{v.due_text}</dd></>)}
                  {buyer.place_of_supply && (<><dt className="text-slate-500">Place of supply</dt><dd>{buyer.place_of_supply}</dd></>)}
                </dl>
              </div>

              {v.inventory.length > 0 && (
                <div className="border-b border-slate-100 px-8 py-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800 text-left text-xs font-semibold text-white">
                        <th className="px-3 py-2">#</th><th className="px-3 py-2">Item</th>
                        <th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.inventory.map((it, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                          <td className="px-3 py-2">{it.item_name}{it.godowns?.[0]?.godown && <div className="text-xs text-slate-500">{it.godowns.map((g) => g.godown).filter(Boolean).join(", ")}</div>}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{it.billed_qty ?? it.qty} {it.unit || ""}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{amount(it.rate)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{amount(it.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totals && (
                    <dl className="ml-auto mt-3 grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm">
                      <dt className="text-slate-600">Sub total</dt><dd className="text-right tabular-nums">{amount(totals.subTotal)}</dd>
                      {totals.charges.map((c, i) => (
                        <Fragment key={i}><dt className="text-slate-600">{c.name}</dt><dd className="text-right tabular-nums">{amount(c.amount)}</dd></Fragment>
                      ))}
                      <dt className="mt-1 border-t border-slate-200 pt-2 font-semibold">Total</dt>
                      <dd className="mt-1 border-t border-slate-200 pt-2 text-right font-semibold tabular-nums">₹{amount(v.amount)}</dd>
                    </dl>
                  )}
                </div>
              )}

              <div className="px-8 py-6">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Accounting entries</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="py-2 text-left">Ledger</th><th className="py-2 text-right">Debit</th><th className="py-2 text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedLines(v.lines).map((l, i) => (
                      <tr key={i} className="border-b border-slate-100 align-top">
                        <td className="py-2">
                          {l.ledger_guid ? <Link to={`/accounts/${l.ledger_guid}`} className="text-blue-700 hover:underline">{l.ledger}</Link> : l.ledger}
                          {l.bills.map((b, j) => (
                            <div key={j} className="text-xs text-slate-500">{b.type}{b.name ? ` · ${b.name}` : ""} · ₹{amount(b.amount)}</div>
                          ))}
                          {l.bank.map((b, j) => (
                            <div key={j} className="text-xs text-slate-500">
                              {[b.transaction_type, b.instrument_number].filter(Boolean).join(" ")}{b.bank_date ? ` · cleared ${dateLabel(b.bank_date)}` : " · not reconciled"}
                            </div>
                          ))}
                        </td>
                        <td className="py-2 text-right tabular-nums">{l.debit ? amount(l.debit) : ""}</td>
                        <td className="py-2 text-right tabular-nums">{l.credit ? amount(l.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-right tabular-nums">{amount(v.total_debit)}</td>
                      <td className="py-2 text-right tabular-nums">{amount(v.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
                {v.narration && <div className="mt-4 text-sm"><span className="text-slate-500">Narration: </span>{v.narration}</div>}
                <div className="mt-6 text-xs text-slate-400">Read-only copy from Tally. To change it, alter the voucher in TallyPrime.</div>
              </div>
            </div>
          );
        })()}
      </Loaded>
    </div>
  );
}
